import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { EmpresaTenant, MonitoramentoItem, SLAStatus } from './models';
import workflowService from './workflowService';
import { alertaPadrao, emailValido, textoHoras, tipoAlertaDoItem, TipoAlerta } from './alertaMonitoramento';
import { extrairMensagemErro } from './mensagemErro';

const money = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

// Extrai "MM/yyyy" direto da string ISO, sem passar por `new Date(...)` — evita o problema
// de fuso horário (UTC-3 volta um dia numa data-only ISO). Mesmo padrão do resto do módulo.
const periodoValueFromIso = (iso: string) => `${iso.slice(5, 7)}/${iso.slice(0, 4)}`;

interface PeriodoOpt { value: string; label: string; }

const scopeLabel = { GROUP: 'Grupo', SUBGROUP: 'Subgrupo', SINGLE: 'Individual' };

const Semaforo: React.FC<{ status: SLAStatus; horas: number }> = ({ status, horas }) => {
  const cfg = {
    VERDE:    { cor: '#1bc5bd', label: 'No prazo',  bg: '#e5faf7' },
    AMARELO:  { cor: '#ffa800', label: 'Próximo',   bg: '#fff4de' },
    VERMELHO: { cor: '#f64e60', label: 'Vencido',   bg: '#fff1f2' },
  }[status];
  return <div className='wf-semaforo' style={{ background: cfg.bg, borderColor: cfg.cor }}>
    <span className='wf-semaforo__dot' style={{ background: cfg.cor }} />
    <div><strong style={{ color: cfg.cor }}>{cfg.label}</strong><small>{textoHoras(horas)}</small></div>
  </div>;
};

const AlertaModal: React.FC<{ item: MonitoramentoItem; tipo: TipoAlerta; onClose: () => void; onEnviado: (item: MonitoramentoItem) => void }> = ({ item, tipo, onClose, onEnviado }) => {
  const padrao = alertaPadrao(item, tipo);
  const [nome, setNome] = useState(padrao.destinatarioNome);
  const [email, setEmail] = useState(padrao.destinatarioEmail);
  const [assunto, setAssunto] = useState(padrao.assunto);
  const [mensagem, setMensagem] = useState(padrao.mensagem);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  const enviar = async () => {
    if (!emailValido(email)) { setErro('Informe um e-mail de destinatário válido.'); return; }
    setErro('');
    setEnviando(true);
    try {
      await workflowService.enviarAlertaManual(item.conciliacaoId, {
        tipo, destinatarioNome: nome.trim(), destinatarioEmail: email.trim(), assunto: assunto.trim(), mensagem: mensagem.trim(),
      });
      onEnviado(item);
    } catch (err: any) {
      setErro(extrairMensagemErro(err, 'Não foi possível enviar o alerta. Tente novamente.'));
    } finally {
      setEnviando(false);
    }
  };

  return <div className='wf-modal-bg'><div className='wf-modal wf-modal--wide'>
    <div className='wf-modal__header'>
      <div><h2>Enviar alerta — {tipo === 'A' ? 'Disponibilidade' : 'Vencimento (Urgente)'}</h2><p>Revise ou edite o destinatário, o e-mail e o texto antes de enviar.</p></div>
      <button onClick={onClose} aria-label='Fechar'>×</button>
    </div>
    <div className='wf-modal__body'>
      <div className='wf-form-grid'>
        <label>Destinatário<input value={nome} onChange={e => { const v = e.target.value; setNome(v); }} placeholder='Nome do destinatário' /></label>
        <label>E-mail do destinatário *<input type='email' value={email} onChange={e => { const v = e.target.value; setEmail(v); }} placeholder='nome@empresa.com.br' /></label>
        <label style={{ gridColumn: '1/-1' }}>Assunto<input value={assunto} onChange={e => { const v = e.target.value; setAssunto(v); }} /></label>
        <label style={{ gridColumn: '1/-1' }}>Mensagem<textarea rows={6} value={mensagem} onChange={e => { const v = e.target.value; setMensagem(v); }} /></label>
      </div>
      {item.escalado && <div className='wf-review'><strong>Escalonamento ativo</strong><p>Cópia enviada ao superior hierárquico N{item.nivel + 1}.</p></div>}
      {erro && <p className='wf-danger-text' role='alert' style={{ marginTop: 10 }}>{erro}</p>}
    </div>
    <div className='wf-modal__footer'>
      <button className='wf-btn wf-btn--secondary' onClick={onClose} disabled={enviando}>Cancelar</button>
      <button className='wf-btn wf-btn--primary' onClick={enviar} disabled={enviando || !email.trim()}>{enviando ? 'Enviando...' : 'Enviar alerta'}</button>
    </div>
  </div></div>;
};

const MonitoramentoPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<{ empresaId?: string; periodo?: string; tab?: string } | undefined>();
  const [empresas, setEmpresas] = useState<EmpresaTenant[]>([]);
  const [empresaId, setEmpresaId] = useState(location.state?.empresaId ?? '');
  const [periodos, setPeriodos] = useState<PeriodoOpt[]>([]);
  const [periodo, setPeriodo] = useState(location.state?.periodo ?? '');
  const [items, setItems] = useState<MonitoramentoItem[]>([]);
  const [counts, setCounts] = useState({ verde: 0, amarelo: 0, vermelho: 0 });
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<'TODAS' | 'PENDENTES' | 'VENCIDAS'>(location.state?.tab === 'VENCIDAS' ? 'VENCIDAS' : 'TODAS');
  const [scopeFilter, setScopeFilter] = useState<'TODOS' | 'GROUP' | 'SUBGROUP' | 'SINGLE'>('TODOS');
  const [templateModal, setTemplateModal] = useState<{ item: MonitoramentoItem; tipo: TipoAlerta } | null>(null);
  const [toast, setToast] = useState('');

  useEffect(() => {
    workflowService.listarTenants()
      .then(lista => setEmpresas(lista.filter(e => e.grupoId != null && e.status === 'ATIVA')))
      .catch(() => setEmpresas([]));
  }, []);

  useEffect(() => {
    if (!empresaId) { setPeriodos([]); setPeriodo(''); return; }
    const emp = empresas.find(e => String(e.id) === empresaId);
    if (emp?.empresaId) {
      workflowService.getPeriodosEmpresa(emp.empresaId)
        .then(ps => setPeriodos(ps.map(p => ({ value: periodoValueFromIso(p.periodoInicio), label: p.label }))))
        .catch(() => setPeriodos([]));
    } else {
      setPeriodos([]);
    }
  }, [empresaId, empresas]);

  useEffect(() => {
    if (!empresaId || !periodo) { setItems([]); setCounts({ verde: 0, amarelo: 0, vermelho: 0 }); return; }
    setLoading(true);
    workflowService.listarMonitoramento(Number(empresaId), periodo).then(res => {
      setItems(res.itens);
      setCounts({ verde: res.verde, amarelo: res.amarelo, vermelho: res.vermelho });
    }).catch(() => {}).finally(() => setLoading(false));
  }, [empresaId, periodo]);

  const isReady = !!empresaId && !!periodo;

  const filtered = items
    .filter(i => scopeFilter === 'TODOS' || i.scopeType === scopeFilter)
    .filter(i => tab === 'TODAS' ? true : tab === 'PENDENTES' ? i.slaStatus !== 'VERMELHO' : i.slaStatus === 'VERMELHO')
    .sort((a, b) => a.horasRestantes - b.horasRestantes);

  return <div className='wf-page'>
    <div className='wf-breadcrumb'>Home &rsaquo; Workflow SOX &rsaquo; Monitoramento / SLA</div>
    <div className='wf-header'>
      <div><h1>Monitoramento de Aprovações e SLA</h1><p>Visibilidade em tempo real do status das conciliações por escopo e semáforo.</p></div>
      <button className='wf-btn wf-btn--secondary' onClick={() => history.goBack()}>Voltar</button>
    </div>

    <section className='wf-card' style={{ marginBottom: 20 }}>
      <div className='wf-search'>
        <select value={empresaId} onChange={e => { setEmpresaId(e.target.value); setPeriodo(''); }}>
          <option value=''>— Selecione a empresa —</option>
          {empresas.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
        </select>
        <select value={periodo} onChange={e => setPeriodo(e.target.value)} disabled={!empresaId || periodos.length === 0}>
          <option value=''>
            {!empresaId ? 'Selecione a empresa primeiro' : periodos.length === 0 ? 'Sem períodos disponíveis' : '— Selecione o período —'}
          </option>
          {periodos.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </div>
    </section>

    {!isReady ? (
      <section className='wf-card' style={{ textAlign: 'center', padding: '48px 24px', color: '#a1a5b7' }}>
        {!empresaId ? 'Selecione uma empresa para visualizar o monitoramento.' : 'Selecione o período.'}
      </section>
    ) : <>
    {loading && <p className='wf-count'>Carregando...</p>}
    <div className='wf-metrics wf-metrics--three'>
      <div className='wf-metric' style={{ borderTopColor: '#1bc5bd' }}><span>No prazo</span><strong style={{ color: '#1bc5bd' }}>{counts.verde}</strong><small>SLA dentro do limite</small></div>
      <div className='wf-metric' style={{ borderTopColor: '#ffa800' }}><span>Próximo do vencimento</span><strong style={{ color: '#ffa800' }}>{counts.amarelo}</strong><small>Menos de 24h restantes</small></div>
      <div className='wf-metric' style={{ borderTopColor: '#f64e60' }}><span>Vencidas</span><strong style={{ color: '#f64e60' }}>{counts.vermelho}</strong><small>SLA expirado — ação imediata</small></div>
    </div>

    <section className='wf-card'>
      <div className='wf-card__header'>
        <div style={{ display: 'flex', gap: 8 }}>
          {(['TODAS', 'PENDENTES', 'VENCIDAS'] as const).map(t => <button key={t} className={`wf-btn ${tab === t ? 'wf-btn--primary' : 'wf-btn--secondary'} wf-btn--sm`} onClick={() => setTab(t)}>{t}</button>)}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#5e6278' }}>Escopo
            <select value={scopeFilter} onChange={e => setScopeFilter(e.target.value as any)} style={{ marginLeft: 8, minHeight: 34, padding: '4px 8px', border: '1px solid #dfe3ea', borderRadius: 7, fontSize: 12 }}>
              <option value='TODOS'>Todos</option>
              <option value='GROUP'>Grupo</option>
              <option value='SUBGROUP'>Subgrupo</option>
              <option value='SINGLE'>Individual</option>
            </select>
          </label>
        </div>
      </div>

      <div className='wf-table-wrap'><table className='wf-table'>
        <thead><tr><th>SLA</th><th>Empresa / Grupo</th><th>Escopo</th><th>Valor</th><th>Etapa / Nível</th><th>Responsável</th><th>Prazo</th><th>Alerta</th><th /></tr></thead>
        <tbody>{filtered.map(item => <tr key={item.id}>
          <td><Semaforo status={item.slaStatus} horas={item.horasRestantes} /></td>
          <td><strong>{item.empresa}</strong><span>{item.grupoNome}</span></td>
          <td><span className='wf-badge wf-badge--em_analise'>{scopeLabel[item.scopeType]}</span></td>
          <td>{money(item.valor)}</td>
          <td><strong>{item.etapa}</strong><span>N{item.nivel}</span></td>
          <td>{item.responsavel}{item.escalado && <span className='wf-badge wf-badge--rejeitada' style={{ marginLeft: 6, fontSize: 9 }}>Escalado</span>}</td>
          <td style={{ fontSize: 11 }}>{item.dataLimite}</td>
          <td>
            {item.alertaEnviado && <span title='E-mail já enviado' style={{ marginRight: 8 }}>✉ Enviado</span>}
            <button className='wf-link' onClick={() => setTemplateModal({ item, tipo: tipoAlertaDoItem(item) })}>{item.alertaEnviado ? 'Reenviar' : 'Enviar alerta'}</button>
          </td>
          <td><button className='wf-link' onClick={() => history.push(`/workflow-aprovacao/conciliacoes/${item.conciliacaoId}`)}>Abrir</button></td>
        </tr>)}</tbody>
      </table></div>
    </section>
    </>}

    {toast && <div className='wf-toast wf-toast--success'>{toast}</div>}
    {templateModal && <AlertaModal item={templateModal.item} tipo={templateModal.tipo} onClose={() => setTemplateModal(null)}
      onEnviado={enviado => {
        setItems(prev => prev.map(i => i.id === enviado.id ? { ...i, alertaEnviado: true } : i));
        setTemplateModal(null);
        setToast('Alerta enviado com sucesso.');
        setTimeout(() => setToast(''), 5000);
      }} />}
  </div>;
};

export default MonitoramentoPage;
